// @flow
//
// `@uniflowed/router/action`: a server action, as the browser holds it.
//
// A `"use client"` module writes an ordinary import and an ordinary call:
//
//   // app/counter/_components/Counter.js
//   "use client";
//   import { recordClick } from "../_actions/clicks.js";
//   …
//   <button onClick={() => { void recordClick(count); }}>
//
// On the server that import is the function. In the browser it is a reference
// built here: `@uniflowed/vite` replaces the `"use server"` module, in the
// client graph only, with one `createServerReference` per callable export, so
// what crosses the import is an id and a `fetch` — and the module's body, its
// imports and every secret they reached stay where they were written.
//
// # Why this module is where it is
//
// It is the browser's half, so it may not live in `@uniflowed/server`: that
// package is in `SERVER_ONLY_PACKAGES` and importing it from a client
// component is the error the RSC graph exists to produce. It holds no
// platform API beyond `fetch` and `location`, and it imports one internal
// module, which is the grammar the server applies to the same bytes.
//
// # The call
//
// `POST` to the page's own URL, `uf-action: <id>`, `application/json`, and
// `{"args":[…]}`. The URL is the page rather than a path uf reserves so that
// the middleware guarding that path runs above the call exactly as it does
// above the page — and it is not a substitute for the action authorizing
// itself; `internal/action-endpoint.js` says why in the paragraph that matters.
//
// # A form
//
// The same reference is what React 19's form APIs take, because a reference is
// an ordinary async function and that is all they ask for:
//
//   // app/counter/_components/Note.js
//   "use client";
//   import { useActionState } from "@uniflowed/react";
//   import { useFormStatus } from "react-dom";
//   import { submitNote } from "../_actions/notes.js";
//
//   component Save() {
//     const { pending } = useFormStatus();
//     return <button disabled={pending}>Save</button>;
//   }
//
//   component Note() {
//     const [saved, save] = useActionState(submitNote, null);
//     return (
//       <form action={save}>
//         <input name="note" />
//         <Save />
//         <output>{saved}</output>
//       </form>
//     );
//   }
//
// `<form action={save}>` hands the reference a `FormData`, `useActionState`
// hands it the previous state and then the `FormData`, and both cross under
// the grammar in `internal/action-wire.js` — one form per call, beside the
// values rather than inside one, entries of strings and nothing else. Flow
// still reads `submitNote`'s own declaration, so a form action whose first
// parameter is not the state it was given `null` for is a `uf check` error.
//
// **It works before the page has hydrated, too.** React's progressive
// enhancement — the form that submits before its JavaScript has arrived — asks
// the function for `$$FORM_ACTION` while rendering to HTML, and a reference
// has one: `method="POST"`, `application/x-www-form-urlencoded`, and hidden
// fields naming the action and carrying its bound arguments. The server
// renders with the real function, which `@uniflowed/vite` gives the same
// property through `registerServerAction` below, so the markup is the same
// whichever side wrote it. A native post is not the JSON call: it is a
// separate door in `./internal/action-endpoint.js`, which accepts only that
// content type, only from the page's own origin, and only with the fields
// `./internal/form-action.js` writes. `useActionState` works there as well
// — the answer is the page rendered with the action's result as the hook's
// state. See ubugeeei-prod/uf#1358.
//
// # After a deploy
//
// A reference carries its build's id, and a new build mints new ids, so a tab
// left open across a deploy holds ids the live server has never heard of. The
// call names the page's build in `uf-deployment`; a server on another build
// answers `409` without running anything; and the reference then loads the
// page's document again — a hard navigation onto the live build — instead of
// throwing. The promise it returned never settles, because the page it was
// returned to is being replaced. See `./internal/deployment.js`.
//
// # What Flow checks, and where
//
// Flow reads `app/_actions/clicks.js`, not the reference the bundler
// substitutes for it, so `recordClick("nine")` against
// `recordClick(count: number)` is a `uf check` error rather than a `400`. That
// is the half that needs nothing from this module.
//
// What a declaration alone does not say is whether those arguments and that
// result can cross a wire at all, and `ActionArguments` and `ActionResult` are
// that half: `uf prepare` writes one instantiation of each into
// `server-actions.js`, over every action in the project at once, so an action
// taking a callback or returning a `Map` is a `uf check` error naming the
// offending type. Without them the same mistake is a request that arrives with
// `{}` where an object was passed — the kind of bug found in production by a
// column that stopped being written.

import {
  ACTION_CONTENT_TYPE,
  ACTION_HEADER,
  ACTION_OUTCOME_HEADER,
  type ActionArgument,
  type ActionValue,
  decodeActionResult,
  encodeActionArguments,
} from "./internal/action-wire.js";
import { loadDocument, refusedAsAnotherDeployment, withDeployment } from "./internal/deployment.js";
import { withFormAction } from "./internal/form-action.js";
import { clearNavigationCache } from "./internal/navigation-cache.js";
import { ForbiddenError, NotFoundError, UnauthorizedError } from "./internal/routing.js";

export type { ActionArgument, ActionValue } from "./internal/action-wire.js";
export {
  ACTION_CONTENT_TYPE,
  ACTION_HEADER,
  ActionValueError,
  MAX_ACTION_ARGUMENTS,
  MAX_ACTION_BODY_BYTES,
  MAX_ACTION_DEPTH,
  MAX_ACTION_VALUES,
  MAX_FORM_ENTRIES,
  MAX_FORM_NAME_LENGTH,
} from "./internal/action-wire.js";

/**
 * A function that can be a server action.
 *
 * Both halves of the signature are the wire grammar, and they are not the same
 * half: an action is called with values that can cross *or* the one form a
 * submit produces, and answers with a value that can cross. `void` is a result
 * and not an argument, because JSON has no `undefined` and an action declared
 * to take one would be taking something the caller cannot send; a `FormData`
 * is an argument and not a result, because a form is something a browser
 * submits and not something a server answers with.
 */
export type ServerActionFunction = (...args: Array<ActionArgument>) => Promise<ActionValue | void>;

/**
 * An action's arguments, held against what a wire can carry.
 *
 * A bound on a tuple rather than a bound on the function, and the difference
 * is the whole reason this is two types instead of one. A function type puts
 * its parameters in a contravariant position: `F extends (…args:
 * Array<ActionValue>) => …` asks whether `F` accepts *every* `ActionValue`,
 * which `createUser(name: string)` does not and should not. `Parameters<F>` is
 * the same list read covariantly, where the question is the one worth asking —
 * is each argument something that can cross?
 *
 * `uf prepare` writes one instantiation over the whole project:
 *
 *   export type ServerActionArgsFitTheWire =
 *     ActionArguments<ServerActionArgs<ServerActionName>>;
 *
 * `ServerActionName` is every action's name, so `ServerActionArgs` of it is
 * every action's argument list, and one line checks all of them.
 *
 * The bound is [`ActionArgument`] and not [`ActionValue`], which is the whole
 * of what makes `<form action={fn}>` type-check: a form action's parameter is
 * a `FormData`, and a `FormData` crosses as an argument and only as one.
 *
 * # What this does not catch, and why
 *
 * The bound is element-wise, so it holds each argument against the grammar and
 * says nothing about the list as a whole. The wire has one rule that is about
 * the list: a call carries at most one form, because the envelope names the
 * form's position once. So `(a: FormData, b: FormData)` type-checks here and
 * throws `ActionValueError` at `encodeActionArguments` — a rule enforced at
 * run time that the types ought to have caught.
 *
 * Saying it in the type needs a walk over the tuple, and the walk is blocked by
 * ubugeeei-prod/uf#300: a spread in a conditional type's tuple pattern binds
 * `infer` as `unknown` and the rest as the whole array widened. The obvious
 * recursion is not merely rejected, it quietly answers wrongly —
 *
 *   type NoForm<T> = T extends [] ? true
 *     : T extends [infer H, ...infer R]
 *       ? (H extends FormData ? false : NoForm<R>) : true;
 *
 * — gives `true` for `[string, FormData]`, because the spread pattern never
 * matches and every tuple falls through to the last branch. A constraint built
 * on that would be worse than none: it would report every signature as fine.
 *
 * `tests/type-tests/server-actions.js` pins the gap, so that whoever fixes
 * #300 is told this is waiting on it. The run-time guard and its test are in
 * `internal/action-wire.js` and `tests/library/server-actions.test.js`.
 */
export type ActionArguments<TArgs extends $ReadOnlyArray<ActionArgument>> = TArgs;

/**
 * An action's result, held against the same grammar.
 *
 * A promise, because a server action is always async — the RSC graph rejects
 * one that is not — and `void` is allowed because an action that returns
 * nothing is ordinary. `Promise` is covariant in Flow, so the bound reaches
 * the resolved type without any of the contortion the arguments needed.
 */
export type ActionResult<TResult extends Promise<ActionValue | void>> = TResult;

/**
 * A server action that did not answer.
 *
 * Carries the status and the action's build-time name and nothing else,
 * because nothing else came back: the endpoint answers every refusal with a
 * fixed body, so there is no message from the server to relay. What went wrong
 * is in the server's log, which is where an application's internals belong.
 */
export class ServerActionError extends Error {
  /** The HTTP status the endpoint answered with. */
  status: number;
  /** `module#export` of the action that was called. */
  action: string;

  constructor(action: string, status: number) {
    super(
      `@uniflowed/router: the server action \`${action}\` answered ${String(status)}. ` +
        "The endpoint reports every failure the same way; the reason is in the server's log.",
    );
    this.name = "ServerActionError";
    this.status = status;
    this.action = action;
  }
}

/**
 * The reference the client bundle holds in place of one server action.
 *
 * Generated, never written by hand: `@uniflowed/vite` emits one call per
 * callable export of a `"use server"` module, with the id
 * `crates/uf_rsc/src/action.rs` derived for it and the `module#export` name
 * that only ever appears in an error.
 *
 * The returned function is `async` and refuses before it sends: an argument
 * outside the wire grammar throws an `ActionValueError` naming the argument's
 * position, at the call site, rather than becoming a `400` with nothing in it.
 *
 * It carries `$$FORM_ACTION`, so a form bound to it is a real form before the
 * page hydrates; see the header and `./internal/form-action.js`.
 */
export function createServerReference(id: string, name: string): ServerActionFunction {
  return withFormAction(callServerActionFor(id, name), id, []);
}

/**
 * Give a server action, on the server, what its reference has in the browser.
 *
 * `@uniflowed/vite` calls this on every callable export of a `"use server"`
 * module in the server graphs, with the id the build derived for it, so that a
 * client component rendered to HTML writes a form that posts without
 * JavaScript. It changes nothing about calling the function: the properties it
 * defines are ones only React reads, and `bind` still binds. Anything that is
 * not a function is returned untouched, because the RSC graph has already
 * refused a `"use server"` export that is not one and this is not the place to
 * say it twice.
 */
export function registerServerAction<T>(fn: T, id: string): T {
  if (typeof fn !== "function") {
    return fn;
  }
  // `typeof` refines `T` to a function whose parameters Flow cannot name, and
  // `withFormAction` changes nothing about calling it; the cast says that.
  withFormAction(fn as $FlowFixMe, id, []);
  return fn;
}

/**
 * Call the server reference a Flight payload handed a client component.
 *
 * A Server Component may pass a `"use server"` function to a Client Component
 * as a prop. In the rsc graph each callable export is registered with React's
 * `registerServerReference` under its action id (`registerServerFunction` in
 * `./rsc.js`), so Flight writes it as a server reference rather than refusing a
 * function. React's browser client decodes that reference into a function
 * that calls back here with the reference's id and the arguments, including
 * any `.bind(null, …)` bound on the server, which React has already placed
 * first. `hydrateFlight` installs this as that callback.
 *
 * `reference` is what `registerServerReference` wrote: the action id, `#`, and
 * the `module#export` name. The call is the same one an imported reference
 * makes, over the same JSON wire, with the same grammar, `Origin` check and
 * deployment header. There is no second wire. An argument outside the grammar,
 * a bound one included, is an `ActionValueError` before anything is sent. See
 * ubugeeei-prod/uf#1359.
 */
export function callServerReference(
  reference: string,
  args     : $ReadOnlyArray<mixed>,
): Promise<ActionValue | void> {
  const hash = reference.indexOf("#");
  const id = hash === -1 ? reference : reference.slice(0, hash);
  const name = hash === -1 ? reference : reference.slice(hash + 1);
  return sendServerAction(id, name, args);
}

/** The network call one reference makes. */
function callServerActionFor(id: string, name: string): ServerActionFunction {
  return function callServerAction(...args: Array<ActionArgument>): Promise<ActionValue | void> {
    return sendServerAction(id, name, args);
  };
}

/** Call action `id`, named `name` in an error, with `args`. */
async function sendServerAction(
  id  : string,
  name: string,
  args: $ReadOnlyArray<mixed>,
): Promise<ActionValue | void> {
  const body = encodeActionArguments(args);
  // The page the call is made from, kept for the answer: a relative redirect
  // means relative to this page, whatever the visitor has done since.
  const from = currentUrl();
  // Built rather than written as a literal, because the header's name is a
  // constant and a computed key in an object literal is a shape Flow
  // declines to track.
  const headers: { [string]: string } = { "content-type": ACTION_CONTENT_TYPE };
  headers[ACTION_HEADER] = id;
  // Which build this page is, so a server on another build refuses the call
  // rather than looking this id up in a table it was never in.
  withDeployment(headers);
  const response = await fetch(from, {
    method: "POST",
    // Stated rather than left to the default, because the default is what a
    // reader has to look up and because this one is load-bearing: the
    // endpoint's `Origin` check is only meaningful for a request that
    // carries the visitor's cookies in the first place.
    credentials: "same-origin",
    // Never a cached answer, and never one written to a cache: an action is
    // a side effect, and `POST` responses are outside HTTP caching by
    // default only until something decides otherwise.
    cache: "no-store",
    headers,
    body,
  });
  // A route a navigation kept may no longer show what this action wrote, and
  // which routes is the server's to know, so every kept route is asked for
  // again. Whatever the status: an action that failed part-way may have
  // written before it failed. See `./internal/navigation-cache.js`.
  clearNavigationCache();
  // The server is on another build: nothing ran, and nothing on this page
  // can be called correctly any more. Load the page again, from the build
  // that is live, and never settle — a rejection here would reach an error
  // boundary for the moment before the document is replaced, and a result
  // would be a lie. See `./internal/deployment.js`.
  if (refusedAsAnotherDeployment(response)) {
    loadDocument(currentUrl());
    return new Promise<ActionValue | void>(() => {});
  }
  // The action called `redirect()`, `notFound()`, `unauthorized()` or
  // `forbidden()`. Checked before the status, because three of the four are
  // not `ok` and none of them is a failure.
  const outcome = response.headers.get(ACTION_OUTCOME_HEADER);
  if (outcome != null) {
    void response.body?.cancel();
    return followOutcome(name, outcome, response, from);
  }
  if (!response.ok) {
    throw new ServerActionError(name, response.status);
  }
  return decodeActionResult(await response.text());
}

/**
 * Do in the browser what the action's routing call asked for.
 *
 * - `redirect`: go to the answer's `Location` the way `router.push` would. The
 *   call then resolves with `undefined`, because the action returned nothing;
 *   it threw. It resolves only once the navigation has settled, so a
 *   `useActionState` on a page that is still on screen afterwards (a redirect
 *   to the same page) is not left pending.
 * - `not-found`, `unauthorized`, `forbidden`: throw the same error the server
 *   caught. React hands an error thrown by an action to the nearest error
 *   boundary, and uf's route boundary already tells `unauthorized()` and
 *   `forbidden()` apart. `notFound()` arrives as a `NotFoundError`, which the
 *   boundary answers with the URL's not-found page rather than its error view
 *   (ubugeeei-prod/uf#1489).
 *
 * Any other value is a server this reference does not understand, and it is
 * the same `ServerActionError` as any other failure.
 *
 * The router is imported when a redirect happens, not at module scope. This
 * module is also imported by the server graphs (`registerServerAction`), and
 * the router's runtime is client-rendering code that has no place there.
 */
async function followOutcome(
  name    : string,
  outcome : string,
  response: Response,
  from    : string,
): Promise<ActionValue | void> {
  switch (outcome) {
    case "redirect": {
      const { followActionRedirect } = await import("./internal/runtime.js");
      await followActionRedirect(response.headers.get("location") ?? from, from);
      return undefined;
    }
    case "not-found":
      throw new NotFoundError();
    case "unauthorized":
      throw new UnauthorizedError();
    case "forbidden":
      throw new ForbiddenError();
    default:
      throw new ServerActionError(name, response.status);
  }
}

/**
 * The URL an action call is sent to: the page the browser is on.
 *
 * Path and query, not the whole URL, so the request is same-origin by
 * construction rather than by a comparison somebody could get wrong. The hash
 * is left off because it never reaches a server.
 *
 * A reference only exists in the client bundle — the server imports the real
 * module — so there is no `location` here only if something has imported the
 * browser's half into a server, and saying so is better than posting to a
 * relative path that means nothing there.
 */
function currentUrl(): string {
  const location = globalThis.location;
  if (location == null) {
    throw new Error(
      "@uniflowed/router: a server action reference was called where there is no `location`. " +
        "A reference is the browser's half of an action; the server imports the module itself.",
    );
  }
  return `${location.pathname}${location.search}`;
}
